<template>
  <div class="filter-selector-parts">
    <div
      class="filter-selector-parts__select"
      :class="{ 'filter-selector-parts__select--current': filterListEnabled }"
      @click="enabledFilter"
    >
      <span class="filter-selector-parts__select__label">{{ filterLabel }}</span>

      <svg
        class="filter-selector-parts__select__icon"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M1.93945 6L7.76268 11.8232C8.44609 12.5066 9.55413 12.5066 10.2375 11.8232L16.0608 6H13.9395L9.17689 10.7626C9.07926 10.8602 8.92097 10.8602 8.82334 10.7626L4.06077 6H1.93945Z"
          fill="white"
        />
      </svg>
    </div>

    <div
      class="filter-selector-parts__mask"
      :class="{ 'filter-selector-parts__mask--current': filterListEnabled }"
      @click="changeFilter(null)"
    />

    <div
      class="filter-selector-parts__list"
      :class="{ 'filter-selector-parts__list--current': filterListEnabled }"
    >
      <button
        class="filter-selector-parts__list__item"
        :class="{ 'filter-selector-parts__list__item--current': selectId === '' }"
        @click="changeFilter('')"
      >
        <span>{{ defaultListItemLabel }}</span>
        <img
          v-if="selectId === ''"
          :src="require('@/assets/img/icon/icon_check_18__green.svg')"
          alt=""
        />
      </button>

      <button
        v-for="(item, i) in filterList"
        class="filter-selector-parts__list__item"
        :class="{ 'filter-selector-parts__list__item--current': selectId === item.id }"
        :key="i"
        @click="changeFilter(item.id, item.type)"
      >
        <span>{{ item.name[$i18n.locale] }}</span>
        <img
          v-if="selectId === item.id"
          :src="require('@/assets/img/icon/icon_check_18__green.svg')"
          alt=""
        />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import I18n from '@/locales/I18n'
import { HighlightTabsType } from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'

/**
 * プルダウンの項目が持つべきフィールドを定義したインタフェース
 */
export interface FilterList {
  id: string
  name: {
    ja: string
    en: string
  }
  type?: string
}

type DataType = {
  filterListEnabled: boolean
  selectId: string
}

/**
 * レース動画再生画面 ハイライト フィルター(セレクター) パーツコンポーネント
 */
export default defineComponent({
  name: 'FilterSelectorParts',
  data(): DataType {
    return {
      filterListEnabled: false,
      selectId: '',
    }
  },
  props: {
    /**
     * 非選択時セレクト テキストデータ
     */
    defaultItemLabel: {
      type: String,
      default: '',
    },
    /**
     * 非選択時リスト 空値テキストデータ
     */
    defaultListItemLabel: {
      type: String,
      default: '',
    },
    /**
     * 選択中のハイライトタブ
     */
    selectedHighlightTab: {
      type: String as PropType<HighlightTabsType>,
      default: '',
    },
    /**
     * フィルターリストデータ
     */
    filterList: {
      type: Array as PropType<FilterList[]>,
      required: true,
    },
  },
  setup(props) {
    const filterLabel = props.defaultItemLabel

    return {
      filterLabel,
    }
  },
  methods: {
    enabledFilter(): void {
      /**
       * フィルター表示切り替え
       */
      this.filterListEnabled = !this.filterListEnabled
      this.$emit('filterEnabled', this.filterListEnabled)
    },
    changeFilter(id: string | null, type?: string): void {
      if (id === null) {
        // プルダウンを閉じるだけの動作
        this.enabledFilter()
        return
      }

      /**
       * フィルター選択切り替え
       */
      this.selectId = id
      const filterData = this.filterList.filter((v) => v.id === this.selectId)
      if (filterData.length !== 0) {
        this.filterLabel = I18n.locale === 'en' ? filterData[0].name.en : filterData[0].name.ja
      } else {
        this.filterLabel = this.defaultItemLabel
      }
      this.$emit('filterChange', this.selectId, type)

      this.enabledFilter()
    },
  },
})
</script>

<style lang="scss" scoped>
.filter-selector-parts {
  &__select {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: auto;
    height: 36px;
    padding: 8px 8px 8px 12px;
    background: $black20;
    @include fontSetTextMedium(13);

    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;

    &__label {
      display: block;
      max-width: calc(100% - 22px);
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      color: $white;
    }

    &__icon {
      opacity: 0.5;
    }

    &--current {
      background: $green-light;

      .filter-selector-parts__select {
        &__label {
          color: $black;
        }

        &__icon {
          opacity: 1;
          transform: rotate(180deg);

          path {
            fill: $black;
          }
        }
      }
    }
  }

  &__mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    width: 100%;
    z-index: 9;
    opacity: 0;
    pointer-events: none;
    transition: all ease 0.3s;

    &--current {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__list {
    position: absolute;
    top: 40px;
    right: 0;
    display: flex;
    flex-flow: column nowrap;
    align-content: flex-start;
    align-items: flex-start;
    justify-content: flex-start;
    justify-items: flex-start;
    row-gap: 1px;
    width: 320px;
    border: 1px solid $gray75;
    border-radius: 8px;
    background: $black;
    z-index: 10;
    overflow-y: auto;
    opacity: 0;
    pointer-events: none;
    transition: all ease 0.3s;
    -webkit-overflow-scrolling: touch;

    &--current {
      opacity: 1;
      pointer-events: auto;
    }

    &__item {
      flex: none;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: 38px;
      padding: 10px 8px 10px 15px;
      color: $white;
      background: $black80;

      + .filter-select-list-parts__list__item {
        margin-top: 1px;
      }

      &--current {
        color: $green-light;
      }

      span {
        display: block;
        max-width: calc(100% - 22px);
        font-size: 13px;
        line-height: 1.38;
        font-weight: 600;
      }
    }
  }
}
</style>

<style lang="scss">
.race-video-highlights-pane,
.race-video-radio-pane {
  &--landscape-primary,
  &--landscape-secondary {
    .filter-selector-parts {
      .filter-selector-parts__list {
        &__item {
          min-height: 34px;
          padding: 8px 8px 8px 15px;
        }
      }
    }
  }
}
</style>
